import { useEffect, useMemo, useState } from 'react';
import { Select, SelectItem, Textarea } from '@nextui-org/react';
import { useTranslation } from 'react-i18next';
import {
  checkType,
  CtyButton,
  CtyShowToast,
  CtySpinner,
} from '@circuitry-ai/doc-ui';
import {
  type ExecuteMapperResponse,
  type MapperItem,
  useExecuteMapperMutation,
} from '@circuitry-ai/doc-data';

interface SchemaMapperExecuteProps {
  readonly mapper: MapperItem | null;
}

interface SumMappingValidationIssue {
  mappingId: string;
  invalidPaths: string[];
}

function formatLabel(value: string) {
  return value
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function renderReadonlyValue(value: unknown, path = 'root'): JSX.Element {
  if (value === null || value === undefined) {
    return <div className="text-sm text-default-400">-</div>;
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return (
      <div className="rounded-[10px] border border-default-200 bg-content1 px-4 py-3 text-sm text-foreground">
        {String(value)}
      </div>
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <div className="text-sm text-default-400">[]</div>;
    }

    return (
      <div className="flex flex-col gap-4">
        {value.map((item, index) => (
          <div
            key={`${path}-${index}`}
            className="rounded-[16px] border border-default-200 bg-content1 p-4"
          >
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-default-500">
              {`#${index + 1}`}
            </div>
            {renderReadonlyValue(item, `${path}-${index}`)}
          </div>
        ))}
      </div>
    );
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) {
      return <div className="text-sm text-default-400">{'{}'}</div>;
    }

    return (
      <div className="flex flex-col gap-4">
        {entries.map(([key, nestedValue]) => (
          <div
            key={`${path}-${key}`}
            className={
              nestedValue !== null &&
              typeof nestedValue === 'object'
                ? 'rounded-[16px] border border-default-200 bg-content1 p-4'
                : 'flex flex-col gap-2'
            }
          >
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-default-500">
              {formatLabel(key)}
            </div>
            {renderReadonlyValue(nestedValue, `${path}-${key}`)}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="rounded-[10px] border border-default-200 bg-default-50 px-3 py-2 text-sm text-foreground">
      {String(value)}
    </div>
  );
}

function safeParseJson(value: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
}

function isFiniteNumberValue(value: unknown) {
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return false;
    return Number.isFinite(Number(trimmed));
  }

  return false;
}

function getValueByPath(
  sourceData: Record<string, unknown>,
  path: string
): unknown {
  if (!path) return undefined;

  return path.split('.').reduce<unknown>((currentValue, segment) => {
    if (!segment) return currentValue;
    if (currentValue === null || currentValue === undefined) return undefined;

    if (Array.isArray(currentValue)) {
      const index = Number(segment);
      if (Number.isInteger(index) && index >= 0 && index < currentValue.length) {
        return currentValue[index];
      }
      return undefined;
    }

    if (typeof currentValue === 'object') {
      return (currentValue as Record<string, unknown>)[segment];
    }

    return undefined;
  }, sourceData);
}

function resolveSumSourcePaths(mapping: Record<string, unknown>): string[] {
  const sourceValue = mapping.source;
  const sourceFieldsValue = mapping.source_fields;
  const sourcePathsValue = mapping.source_paths;
  const inputsValue = mapping.inputs;

  const candidateValues = [
    sourceValue,
    sourceFieldsValue,
    sourcePathsValue,
    inputsValue,
  ];

  const extractedPaths = candidateValues.flatMap((candidateValue) => {
    if (typeof candidateValue === 'string') {
      return [candidateValue];
    }

    if (Array.isArray(candidateValue)) {
      return candidateValue.filter((item): item is string => typeof item === 'string');
    }

    return [];
  });

  return Array.from(new Set(extractedPaths.filter(Boolean)));
}

function isSumMapping(mapping: Record<string, unknown>) {
  const sumKeys = ['transform', 'operation', 'op', 'function', 'type'];
  return sumKeys.some((key) => String(mapping[key] ?? '').toLowerCase() === 'sum');
}

function findInvalidSumMappings(
  mapper: MapperItem | null,
  sourceData: Record<string, unknown>
): SumMappingValidationIssue[] {
  const mappings = mapper?.mapper_definition?.mappings;
  if (!Array.isArray(mappings)) return [];

  return mappings
    .map((mapping, index) => {
      const mappingRecord =
        mapping && typeof mapping === 'object'
          ? (mapping as Record<string, unknown>)
          : null;
      if (!mappingRecord || !isSumMapping(mappingRecord)) return null;

      const sourcePaths = resolveSumSourcePaths(mappingRecord);
      if (sourcePaths.length === 0) return null;

      const invalidPaths = sourcePaths.filter((path) => {
        const value = getValueByPath(sourceData, path);
        return !isFiniteNumberValue(value);
      });

      if (invalidPaths.length === 0) return null;

      return {
        mappingId: String(mappingRecord.id ?? `#${index + 1}`),
        invalidPaths,
      };
    })
    .filter((issue): issue is SumMappingValidationIssue => issue !== null);
}

export default function SchemaMapperExecute({ mapper }: Readonly<SchemaMapperExecuteProps>) {
  const { t } = useTranslation();
  const [sourceJson, setSourceJson] = useState<string>('');
  const [direction, setDirection] = useState<'INBOUND' | 'OUTBOUND'>('INBOUND');
  const [executeResponse, setExecuteResponse] = useState<ExecuteMapperResponse | null>(null);
  const [executeMapper, { isLoading: isExecuting }] = useExecuteMapperMutation();

  const parsedSourceJson = useMemo(() => safeParseJson(sourceJson), [sourceJson]);
  const responseData = useMemo(() => executeResponse?.data ?? executeResponse, [executeResponse]);
  let executeContent: JSX.Element | null = null;

  if (isExecuting) {
    executeContent = (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
        <CtySpinner size="md" />
        <div className="text-sm text-default-500">
          {t('Executing mapper and waiting for response...')}
        </div>
      </div>
    );
  } else if (executeResponse) {
    executeContent = (
      <div className="flex h-full min-h-0 flex-col gap-3">
        <div className="min-h-0 flex-1 overflow-auto p-1">
          {renderReadonlyValue(responseData)}
        </div>
      </div>
    );
  }

  useEffect(() => {
    setExecuteResponse(null);
  }, [mapper?.aiworker_id]);

  const handleExecute = async () => {
    if (!mapper?.aiworker_id) {
      CtyShowToast(checkType.error, t('Mapper ID not found'));
      return;
    }

    if (!parsedSourceJson) {
      CtyShowToast(checkType.error, t('Please enter a valid JSON object'));
      return;
    }

    const invalidSumMappings = findInvalidSumMappings(mapper, parsedSourceJson);
    if (invalidSumMappings.length > 0) {
      const invalidFields = invalidSumMappings
        .flatMap((issue) => issue.invalidPaths)
        .slice(0, 4)
        .join(', ');
      CtyShowToast(
        checkType.error,
        t('Sum mappings require numeric fields. Invalid fields: {{fields}}', {
          fields: invalidFields,
        })
      );
      return;
    }

    try {
      const response = await executeMapper({
        aiworker_id: mapper.aiworker_id,
        direction,
        source_data: parsedSourceJson,
      }).unwrap();
      setExecuteResponse(response);
      CtyShowToast(
        checkType.success,
        t('Mapper executed successfully')
      );
    } catch (error) {
      setExecuteResponse(null);
      CtyShowToast(
        checkType.error,
        (error as Error)?.message ?? t('Failed to execute mapper')
      );
    }
  };

  return (
    <div className="flex h-full flex-col gap-3 px-5 py-3">
      <div className="grid h-full min-h-0 grid-cols-1 gap-4 lg:grid-cols-[minmax(260px,25%)_minmax(0,75%)]">
        <div className="flex h-full min-h-0 flex-col gap-4">
          <div className="text-sm font-semibold text-foreground">
            {t('Source Data')}
          </div>
          <Select
            aria-label={t('Direction')}
            label={t('Direction')}
            selectedKeys={[direction]}
            onChange={(event) =>
              setDirection((event.target.value as 'INBOUND' | 'OUTBOUND') || 'INBOUND')
            }
            classNames={{
              trigger:
                'rounded-[12px] border border-default-200 bg-content1 shadow-none',
            }}
          >
            <SelectItem key="INBOUND" value="INBOUND">
              {t('INBOUND')}
            </SelectItem>
            <SelectItem key="OUTBOUND" value="OUTBOUND">
              {t('OUTBOUND')}
            </SelectItem>
          </Select>
          <Textarea
            aria-label={t('Source JSON input')}
            value={sourceJson}
            onValueChange={setSourceJson}
            minRows={5}
            placeholder={t('Paste source JSON')}
            classNames={{
              inputWrapper:
                'rounded-[12px] border border-default-200 bg-content1 shadow-none',
              input: 'font-mono text-[12px] leading-5',
            }}
          />
          <div className="flex items-center justify-between gap-3 border-t border-default-200 pt-4">
            <div className="text-xs text-danger">
              {!parsedSourceJson && sourceJson.trim()
                ? t('Input must be a valid JSON object')
                : '\u00A0'}
            </div>
            <CtyButton
              color="primary"
              variant="solid"
              onPress={handleExecute}
              isLoading={isExecuting}
              isDisabled={!parsedSourceJson || !mapper?.aiworker_id}
            >
              {t('Execute')}
            </CtyButton>
          </div>
        </div>

        <div className="flex h-full min-h-0 flex-col border-l border-default-200 pl-4">
          <div className="min-h-0 flex-1 overflow-auto">
            {executeContent}
          </div>
        </div>
      </div>
    </div>
  );
}
